
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import QuoteDialog from '@/components/quotes/QuoteDialog';
import { Quote } from '@/types/child';

interface QuotesListProps {
  quotes: Quote[];
  childId: string;
  onQuoteAdded: (newQuote: Quote) => void;
}

const QuotesList: React.FC<QuotesListProps> = ({ quotes, childId, onQuoteAdded }) => {
  const [isQuotesExpanded, setIsQuotesExpanded] = useState(false);
  
  const recentQuotes = quotes.slice(0, 3);
  const remainingQuotes = quotes.slice(3);

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <span>Recent Quotes</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {quotes.length > 0 ? (
          <Collapsible className="space-y-4">
            <div className="space-y-4">
              {recentQuotes.map((quote) => (
                <Link 
                  key={quote.id} 
                  to={`/quotes?highlight=${quote.id}`} 
                  className="block transition-transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
                >
                  <div className="p-4 border rounded-lg hover:border-primary">
                    <p className="italic mb-2 text-left">{quote.content}</p>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                      <span>{new Date(quote.date).toLocaleDateString()}</span>
                      {quote.context && (
                        <>
                          <span>•</span>
                          <span>{quote.context}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            {remainingQuotes.length > 0 && (
              <>
                <CollapsibleContent className="space-y-4">
                  {remainingQuotes.map((quote) => (
                    <Link 
                      key={quote.id} 
                      to={`/quotes?highlight=${quote.id}`}
                      className="block transition-transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg"
                    >
                      <div className="p-4 border rounded-lg hover:border-primary">
                        <p className="italic mb-2 text-left">{quote.content}</p>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                          <span>{new Date(quote.date).toLocaleDateString()}</span>
                          {quote.context && (
                            <>
                              <span>•</span>
                              <span>{quote.context}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </CollapsibleContent>
                
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full flex items-center justify-center gap-2 mt-2"
                    onClick={() => setIsQuotesExpanded(!isQuotesExpanded)}
                  >
                    {isQuotesExpanded ? (
                      <>Show less <ChevronUp className="h-4 w-4" /></>
                    ) : (
                      <>Show all quotes <ChevronDown className="h-4 w-4" /></>
                    )}
                  </Button>
                </CollapsibleTrigger>
              </>
            )}
          </Collapsible>
        ) : (
          <div className="text-center py-6">
            <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No quotes recorded yet</p>
          </div>
        )}
        <div className="mt-4">
          <QuoteDialog onQuoteAdded={onQuoteAdded} defaultChildId={childId}>
            <Button className="w-full">
              Add New Quote
            </Button>
          </QuoteDialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuotesList;
